/**
 * manager-pdp.js
 * Gerencia a Product Detail Page (PDP).
 * Disponível como window._pdpManager (instância atual).
 *
 * Dependências (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 *
 * ─── Módulos ──────────────────────────────────────────────────────────────
 *
 *   SizeSelectorController  → popup de seleção de tamanho
 *   AddToCartController     → intercepta form submit e delega ao CartManager
 *   GalleryObserver         → ativa âncoras do menu lateral ao scrollar
 *                             (opt-in: seção deve ter data-pdp-gallery)
 *
 * ─── Inicialização via Liquid ─────────────────────────────────────────────
 *
 *   Na section pdp-main.liquid, dentro do bloco {% javascript %}:
 *
 *     if (typeof initPdpManager === 'function') initPdpManager('{{ section.id }}');
 *
 *   A seção é selecionada por .pdpMain--{sectionId}.
 *
 * ─── HTML esperado ────────────────────────────────────────────────────────
 *
 *   <section class="pdpMain--{sectionId}" [data-pdp-gallery]>
 *
 *     <!-- Imagens da galeria (GalleryObserver — opt-in via data-pdp-gallery) -->
 *     <div id="product-image-1">...</div>
 *     <div id="product-image-2">...</div>
 *
 *     <!-- Âncoras do menu lateral -->
 *     <a href="#product-image-1" class="pdpMain__bannerMenu">...</a>
 *
 *     <!-- Um ou mais wrappers de produto (ex: produto + produto relacionado) -->
 *     <div data-product-wrapper>
 *
 *       <!-- Seletor de tamanho -->
 *       <div data-size-selector>
 *         <button data-size-toggle aria-expanded="false">Selecionar tamanho</button>
 *         <div data-size-popup hidden>
 *           <button data-size-option data-variant-id="111" data-size-label="P">P</button>
 *           <button data-size-option data-variant-id="222" data-size-label="M">M</button>
 *         </div>
 *         <span data-size-label></span>
 *       </div>
 *
 *       <!-- Formulário de produto -->
 *       <form action="/cart/add">
 *         <input type="hidden" name="id" data-size-input>
 *         <button type="button" data-add-to-cart disabled>Selecione o tamanho</button>
 *       </form>
 *
 *     </div>
 *
 *   </section>
 */

/* ─── SizeSelectorController ──────────────────────────────────────────────── */

class SizeSelectorController {
  constructor(wrapper) {
    this._wrapper  = wrapper;
    this._toggle   = wrapper.querySelector('[data-size-toggle]');
    this._popup    = wrapper.querySelector('[data-size-popup]');
    this._options  = wrapper.querySelectorAll('[data-size-option]');
    this._label    = wrapper.querySelector('[data-size-label]');
    this._input    = wrapper.querySelector('[data-size-input]');
    this._addBtn   = wrapper.querySelector('[data-add-to-cart]');
    this._isOpen   = false;

    this._onDocClick = this._onDocClick.bind(this);
    this._onKeyDown  = this._onKeyDown.bind(this);

    this._init();
  }

  _init() {
    this._toggle?.addEventListener('click', () => this._isOpen ? this._close() : this._open());
    this._options.forEach(opt => opt.addEventListener('click', () => this._select(opt)));
    document.addEventListener('click',   this._onDocClick);
    document.addEventListener('keydown', this._onKeyDown);
  }

  _onDocClick(e) {
    if (!this._wrapper.contains(e.target)) this._close();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this._close();
  }

  _open() {
    this._popup?.classList.add('is-open');
    this._popup?.removeAttribute('hidden');
    this._toggle?.setAttribute('aria-expanded', 'true');
    this._isOpen = true;
  }

  _close() {
    this._popup?.classList.remove('is-open');
    this._popup?.setAttribute('hidden', '');
    this._toggle?.setAttribute('aria-expanded', 'false');
    this._isOpen = false;
  }

  _select(option) {
    const variantId = option.dataset.variantId;
    const label     = option.dataset.sizeLabel || option.textContent.trim();
    if (this._input)  this._input.value        = variantId;
    if (this._label)  this._label.textContent   = label;

    const selectedVariantOption = this._input?.selectedOptions?.[0];
    const variantPrice = selectedVariantOption?.dataset.price;
    const isAvailable = selectedVariantOption?.dataset.available !== 'false';

    // Marca opção ativa
    this._options.forEach(o => o.classList.toggle('is-active', o === option));

    // Habilita o botão de compra na primeira seleção
    if (this._addBtn) {
      this._addBtn.disabled = !isAvailable || (variantPrice !== undefined && Number(variantPrice) <= 0);
      this._addBtn.setAttribute('aria-disabled', String(this._addBtn.disabled));
      this._addBtn.type     = 'submit';
    }

    this._close();
  }

  destroy() {
    document.removeEventListener('click',   this._onDocClick);
    document.removeEventListener('keydown', this._onKeyDown);
  }
}

/* ─── AddToCartController ─────────────────────────────────────────────────── */

class AddToCartController {
  constructor(wrapper) {
    this._form   = wrapper.querySelector('form[action*="/cart/add"]') || wrapper.querySelector('form');
    this._button = wrapper.querySelector('[data-add-to-cart]');
    // Suporta data-size-input (size selector) ou name="id" (select/hidden input padrão)
    this._input  = wrapper.querySelector('[data-size-input]')
                    || wrapper.querySelector('[name="id"]');

    if (!this._form) return;

    this._onSubmit = this._onSubmit.bind(this);
    this._form.addEventListener('submit', this._onSubmit);
  }

  async _onSubmit(e) {
    e.preventDefault();
    const variantId = this._input?.value;
    const selectedVariant = this._input?.selectedOptions?.[0];
    const variantPrice = selectedVariant?.dataset.price;
    if ((variantId && this._button?.disabled) || (variantPrice !== undefined && Number(variantPrice) <= 0)) return;
    if (!variantId) {
      if (this._button) {
        const button = this._button;
        const anchor = button.closest('.pdp-bar__actions, .pdp-mobile-form') || button.parentElement;
        let message = anchor?.querySelector('[data-pdp-size-warning]');

        if (anchor && !message) {
          message = document.createElement('span');
          message.dataset.pdpSizeWarning = '';
          message.textContent = 'Selecione um tamanho';
          anchor.appendChild(message);
        }

        if (message) {
          message.style.setProperty('--pdp-warning-left', `${button.offsetLeft + (button.offsetWidth / 2)}px`);
          message.style.setProperty(
            '--pdp-warning-top',
            `${button.offsetTop - message.offsetHeight - 7}px`
          );
          window.clearTimeout(button._emptyVariantMessageTimer);
          message.classList.remove('is-visible');
          requestAnimationFrame(() => message.classList.add('is-visible'));
          button._emptyVariantMessageTimer = window.setTimeout(() => {
            message.classList.remove('is-visible');
          }, 2200);
        }
      }
      return;
    }

    const action  = this._button?.dataset.addAction || 'open_drawer';
    const cartUrl = this._button?.dataset.cartUrl   || '/cart';

    // Congela dimensões para evitar layout shift ao injetar SVG do ButtonFeedback
    const btn = this._button;
    if (btn) {
      btn.style.width  = `${btn.offsetWidth}px`;
      btn.style.height = `${btn.offsetHeight}px`;
    }

    const result = await window.CartManager?.add(variantId, {
      button: btn,
      action,
      variantPrice,
    });

    if (btn) {
      btn.style.width  = '';
      btn.style.height = '';
    }

    if (result?.success && action === 'open_cart') {
      window.location.href = cartUrl;
    }
  }

  destroy() {
    this._form?.removeEventListener('submit', this._onSubmit);
  }
}

/* ─── GalleryObserver ─────────────────────────────────────────────────────── */

class GalleryObserver {
  constructor(section) {
    this._observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const anchor = section.querySelector(`a.pdpMain__bannerMenu[href="#${entry.target.id}"]`);
          if (!anchor) return;
          anchor.classList.toggle('opacity-100', entry.isIntersecting);
          anchor.classList.toggle('opacity-50',  !entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    section.querySelectorAll('[id^="product-image-"]').forEach(el => {
      this._observer.observe(el);
    });
  }

  destroy() {
    this._observer.disconnect();
  }
}

/* ─── PdpManager ──────────────────────────────────────────────────────────── */

class VariantImageFilterController {
  constructor(section) {
    this._section = section;
    this._filterEnabled = section.dataset.pdpFilterVariantImages === 'true';
    this._hasColorLabeledImages = section.dataset.pdpHasColorLabeledImages === 'true';
    this._mobileSlider = section.querySelector('[data-splide-component="pdp-mobile-images"]');
    this._mobileList = this._mobileSlider?.querySelector('.splide__list');
    this._mobileSlides = this._mobileList ? Array.from(this._mobileList.children) : [];
    this._onChange = this._onChange.bind(this);

    section.addEventListener('change', this._onChange);
    this._apply(section.dataset.pdpSelectedColor);
  }

  _normalizeColor(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  _onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const optionName = input.dataset.pdpOptionName || '';
    if (!/^(cor|color)(-|$)/.test(optionName)) return;

    this._apply(input.value);
  }

  _apply(value) {
    const selectedColor = this._normalizeColor(value);
    const imageElements = Array.from(this._section.querySelectorAll('[data-pdp-image-color]'));
    const hasMatchingImage = imageElements.some((imageElement) => imageElement.dataset.pdpImageColor === selectedColor);
    const shouldFilter = this._filterEnabled
      && this._hasColorLabeledImages
      && selectedColor !== ''
      && hasMatchingImage;
    this._section.dataset.pdpSelectedColor = selectedColor;

    imageElements.forEach((imageElement) => {
      imageElement.hidden = shouldFilter && imageElement.dataset.pdpImageColor !== selectedColor;
    });

    const zoomRoot = this._section.querySelector('[data-pdp-zoom]');
    const zoomInstance = window.PdpZoomManager?.instances.get(zoomRoot);
    if (zoomInstance && !zoomRoot.hidden && zoomInstance.thumbs[zoomInstance.index]?.hidden) {
      const firstVisibleIndex = zoomInstance.thumbs.findIndex((thumb) => !thumb.hidden);
      if (firstVisibleIndex !== -1) zoomInstance.show(firstVisibleIndex, 0, false);
    }

    if (!this._mobileList) return;

    const visibleSlides = shouldFilter
      ? this._mobileSlides.filter((slide) => slide.dataset.pdpImageColor === selectedColor)
      : this._mobileSlides;

    if (this._mobileSlider.splide) {
      this._mobileSlider.splide.destroy();
      delete this._mobileSlider.splide;
    }

    this._mobileSlides.forEach((slide) => {
      if (visibleSlides.includes(slide)) {
        slide.hidden = false;
        this._mobileList.appendChild(slide);
      } else {
        slide.hidden = true;
        slide.remove();
      }
    });

    this._mobileSlider.dataset.splideArrows = visibleSlides.length > 1 ? 'true' : 'false';
    if (window.SplideManager) window.SplideManager.init(this._mobileSlider);
  }

  destroy() {
    this._section.removeEventListener('change', this._onChange);
  }
}

class PdpManager {
  constructor({ sectionId }) {
    this._section = document.querySelector(`.pdpMain--${sectionId}`);
    if (!this._section) return;

    this._modules = [];
    this._modules.push(new VariantImageFilterController(this._section));

    // Um SizeSelectorController + AddToCartController por [data-product-wrapper]
    this._section.querySelectorAll('[data-product-wrapper]').forEach(wrapper => {
      this._modules.push(new SizeSelectorController(wrapper));
      this._modules.push(new AddToCartController(wrapper));
    });

    // GalleryObserver é opt-in via data-pdp-gallery na seção
    if (this._section.hasAttribute('data-pdp-gallery')) {
      this._modules.push(new GalleryObserver(this._section));
    }
  }

  destroy() {
    this._modules.forEach(m => m.destroy?.());
    this._modules = [];
  }
}

/* ─── Inicialização ───────────────────────────────────────────────────────── */

function initPdpManager(sectionId) {
  if (window._pdpManager) window._pdpManager.destroy();
  window._pdpManager = new PdpManager({ sectionId });
}

window.initPdpManager = initPdpManager;

// Auto-inicialização: escaneia o DOM por seções PDP declaradas via data attribute.
// Resolve o problema de timing onde {% javascript %} executa antes deste script.
document.querySelectorAll('[data-pdp-section-id]').forEach(el => {
  initPdpManager(el.dataset.pdpSectionId);
});
